/*
 * SFR MERE-FILLE QUIZ v2. Independent, namespaced fork of the IFL UI pattern.
 * This file does not read or write any IFL DOM selector, asset or state.
 * Each [data-sfrmdq-root] has its own config, state and session ID.
 */
(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-sfrmdq-root]';
  const CONFIG_SELECTOR = '[data-sfrmdq-config]';
  const APP_SELECTOR = '[data-sfrmdq-app]';
  const QUESTION_KINDS = new Set(['single', 'multi', 'slider']);
  const THEME_KEYS = ['strength', 'own_path', 'presence', 'pride'];
  const MESSAGE_THEME = {
    strong: 'strength',
    own_way: 'own_path',
    loved: 'presence',
    proud: 'pride'
  };
  const TRACKING_PARAMS = new Set([
    'fbclid', 'gclid', 'ttclid', 'msclkid',
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term',
    'utm_content', 'utm_id'
  ]);

  const safeParse = (value) => {
    try { return JSON.parse(value); } catch (_) { return null; }
  };
  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
  const normalizedArray = (value) => Array.isArray(value) ? value : value == null ? [] : [value];
  const createSessionId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `sfrmd_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  };

  function initQuiz(root) {
    if (!root || root.dataset.sfrmdqInitialized === 'true') return;
    const configNode = root.querySelector(CONFIG_SELECTOR);
    const app = root.querySelector(APP_SELECTOR);
    if (!configNode || !app) return;

    const config = safeParse(configNode.textContent);
    if (!config || !Array.isArray(config.screens) || !config.screens.length) {
      app.innerHTML = '<div class="sfrmdq-error">Le quiz est temporairement indisponible.</div>';
      return;
    }

    root.dataset.sfrmdqInitialized = 'true';
    if (config.hideThemeChrome) document.body.classList.add('sfrmdq-page');

    const quizSessionId = createSessionId();
    const questionScreens = config.screens.filter(s => QUESTION_KINDS.has(s.kind));
    const questionOrdinal = new Map(questionScreens.map((s, i) => [s.id, i + 1]));
    const state = {
      phase: 'screen',
      screenIndex: 0,
      answers: {},
      answerLabels: {},
      resultId: null,
      startedAt: null,
      completedAt: null
    };
    let lastViewedScreenId = null;
    let completionDataSent = false;

    function emit(name, payload = {}) {
      const params = {
        quiz_id: config.quizId,
        quiz_version: config.version,
        quiz_session_id: quizSessionId,
        ...payload
      };
      // Keep IFL's existing event taxonomy for dashboards, but never share state.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: `fp_${name}`, ...params });
      window.dispatchEvent(new CustomEvent('fp:quiz', { detail: { name, ...params } }));
      if (['QuizStart', 'QuizComplete', 'QuizPDPClick'].includes(name) &&
          typeof window.fbq === 'function') {
        try { window.fbq('trackCustom', name, params); } catch (_) { /* pixel optional */ }
      }
    }

    function ensureStarted() {
      if (state.startedAt) return;
      state.startedAt = new Date().toISOString();
      emit('QuizStart');
    }

    function completedBefore(index) {
      return config.screens.slice(0, index).filter(s =>
        QUESTION_KINDS.has(s.kind) && state.answers[s.id] != null
      ).length;
    }

    function render() {
      if (state.phase === 'processing') return renderProcessing();
      if (state.phase === 'result') return renderResult();
      renderScreen();
    }

    function renderScreen() {
      const screen = config.screens[state.screenIndex];
      if (!screen) return startProcessing();
      const firstQuestion = state.screenIndex === 0;
      const ordinal = questionOrdinal.get(screen.id) || null;
      const pct = Math.round(100 * completedBefore(state.screenIndex) / questionScreens.length);

      app.innerHTML = `
        <main class="sfrmdq-shell">
          ${firstQuestion ? `<div class="sfrmdq-direct-hook">
            <h1 class="sfrmdq-direct-hook__headline">${escapeHtml(config.entry.headline)}</h1>
            <p class="sfrmdq-direct-hook__promise">${escapeHtml(config.entry.promise)}</p>
          </div>` : ''}
          <div class="sfrmdq-topbar">
            ${firstQuestion ? '<span aria-hidden="true"></span>' :
              `<button class="sfrmdq-back" type="button" data-action="back">${escapeHtml(config.ui.back)}</button>`}
            <div class="sfrmdq-progress" role="progressbar" aria-label="Progression du quiz"
                 aria-valuemin="0" aria-valuemax="100" aria-valuenow="${pct}">
              <span style="width:${pct}%"></span>
            </div>
            <div class="sfrmdq-counter">${ordinal ?
              `${escapeHtml(config.ui.questionLabel)} ${ordinal}/${questionScreens.length}` : ''}</div>
          </div>
          <section class="sfrmdq-panel" data-screen-id="${escapeHtml(screen.id)}">
            ${screenHtml(screen)}
          </section>
        </main>`;

      bindScreen(screen);
      if (lastViewedScreenId !== screen.id) {
        emit(screen.kind === 'mid_card' ? 'QuizCardView' : 'QuizQuestionView', {
          screen_id: screen.id,
          screen_kind: screen.kind,
          question_number: ordinal
        });
        lastViewedScreenId = screen.id;
      }
    }

    function screenHtml(screen) {
      if (screen.kind === 'mid_card') {
        const insight = getMidpointInsight();
        return `<div class="sfrmdq-mid">
          <p class="sfrmdq-kicker">${escapeHtml(config.midpoint.eyebrow)}</p>
          <h2 class="sfrmdq-mid-title">${escapeHtml(config.midpoint.headline)}</h2>
          <p class="sfrmdq-copy">${escapeHtml(insight.body)}</p>
          <div class="sfrmdq-mid-note">
            <span class="sfrmdq-mid-note__label">CE QUI RESSORT DE VOS RÉPONSES</span>
            <strong>${escapeHtml(insight.main)}</strong>
          </div>
          ${insight.extra ? `<p class="sfrmdq-mid-secondary">${escapeHtml(insight.extra)}</p>` : ''}
          <div class="sfrmdq-actions sfrmdq-actions--center">
            <button class="sfrmdq-btn" type="button" data-action="continue">${escapeHtml(config.midpoint.cta)}</button>
          </div>
        </div>`;
      }

      if (screen.kind === 'slider') {
        const current = String(state.answers[screen.id] ?? screen.default ?? screen.min);
        const currentLabel = screen.valueLabels?.[current] || current;
        return `<h2 class="sfrmdq-question">${escapeHtml(screen.question)}</h2>
          <div class="sfrmdq-slider-wrap">
            <div class="sfrmdq-slider-feedback">
              <div class="sfrmdq-slider-number" data-slider-value>${escapeHtml(current)}<span> / ${escapeHtml(screen.max)}</span></div>
              <p class="sfrmdq-slider-description" data-slider-description>${escapeHtml(currentLabel)}</p>
            </div>
            <input class="sfrmdq-slider" type="range"
              min="${Number(screen.min)}" max="${Number(screen.max)}"
              step="${Number(screen.step || 1)}" value="${Number(current)}"
              data-slider aria-label="${escapeHtml(screen.question)}"
              aria-valuetext="${escapeHtml(currentLabel)}">
            <div class="sfrmdq-slider-labels">
              <span>${escapeHtml(screen.minLabel)}</span><span>${escapeHtml(screen.maxLabel)}</span>
            </div>
          </div>
          <div class="sfrmdq-actions"><button type="button" class="sfrmdq-btn" data-action="slider-next">${escapeHtml(screen.cta || config.ui.continue)}</button></div>`;
      }

      const selected = screen.kind === 'multi' ? normalizedArray(state.answers[screen.id]) : state.answers[screen.id];
      const answersHtml = screen.answers.map(a => {
        const active = screen.kind === 'multi' ? selected.includes(a.id) : selected === a.id;
        return `<button class="sfrmdq-answer" type="button" data-answer-id="${escapeHtml(a.id)}"
          aria-pressed="${active ? 'true' : 'false'}">
          <span>${escapeHtml(a.label)}</span><span class="sfrmdq-answer__check" aria-hidden="true">✓</span>
        </button>`;
      }).join('');

      const isMulti = screen.kind === 'multi';
      const selectedCount = isMulti ? normalizedArray(state.answers[screen.id]).length : 0;
      const maxCount = Number(screen.maxSelections || screen.answers.length);
      return `<h2 class="sfrmdq-question">${escapeHtml(screen.question)}</h2>
        ${screen.helper ? `<p class="sfrmdq-helper">${escapeHtml(screen.helper)}</p>` : ''}
        <div class="sfrmdq-answers${isMulti ? ' sfrmdq-answers--multi' : ''}">${answersHtml}</div>
        ${isMulti ? `<p class="sfrmdq-choice-count" data-selection-count aria-live="polite">${selectedCount} / ${maxCount} sélectionnée${selectedCount > 1 ? 's' : ''}</p>
          <div class="sfrmdq-inline-error" data-inline-error aria-live="polite"></div>
          <div class="sfrmdq-actions"><button type="button" class="sfrmdq-btn" data-action="multi-next">${escapeHtml(screen.cta || config.ui.continue)}</button></div>` : ''}`;
    }

    function bindScreen(screen) {
      app.querySelector('[data-action="back"]')?.addEventListener('click', goBack);
      if (screen.kind === 'mid_card') {
        app.querySelector('[data-action="continue"]')?.addEventListener('click', nextScreen);
        return;
      }
      if (screen.kind === 'slider') {
        const input = app.querySelector('[data-slider]');
        input?.addEventListener('input', () => {
          const value = input.value;
          const label = screen.valueLabels?.[value] || value;
          const number = app.querySelector('[data-slider-value]');
          const description = app.querySelector('[data-slider-description]');
          if (number) number.innerHTML = `${escapeHtml(value)}<span> / ${escapeHtml(screen.max)}</span>`;
          if (description) description.textContent = label;
          input.setAttribute('aria-valuetext', label);
        });
        app.querySelector('[data-action="slider-next"]')?.addEventListener('click', () => {
          if (!input) return;
          ensureStarted();
          recordAnswer(screen, input.value, screen.valueLabels?.[input.value] || input.value);
          nextScreen();
        });
        return;
      }
      if (screen.kind === 'multi') {
        app.querySelectorAll('[data-answer-id]').forEach(button => {
          button.addEventListener('click', () => toggleMultiAnswer(screen, button.dataset.answerId));
        });
        app.querySelector('[data-action="multi-next"]')?.addEventListener('click', () => {
          const ids = normalizedArray(state.answers[screen.id]);
          if (!ids.length) {
            setInlineError(config.ui.selectAtLeastOne);
            return;
          }
          emit('QuizAnswer', {
            screen_id: screen.id,
            answer_ids: ids,
            answer_labels: ids.map(id => getAnswerLabel(screen, id))
          });
          nextScreen();
        });
        refreshMultiButtons(screen);
        return;
      }
      app.querySelectorAll('[data-answer-id]').forEach(button => {
        button.addEventListener('click', () => {
          const answer = screen.answers.find(a => a.id === button.dataset.answerId);
          if (!answer) return;
          ensureStarted();
          recordAnswer(screen, answer.id, answer.label);
          nextScreen();
        });
      });
    }

    function toggleMultiAnswer(screen, answerId) {
      const answer = screen.answers.find(a => a.id === answerId);
      if (!answer) return;
      ensureStarted();
      let selected = normalizedArray(state.answers[screen.id]);
      if (selected.includes(answerId)) {
        selected = selected.filter(id => id !== answerId);
      } else if (answer.exclusive) {
        selected = [answerId];
      } else {
        const excluded = new Set(screen.answers.filter(a => a.exclusive).map(a => a.id));
        selected = selected.filter(id => !excluded.has(id));
        if (screen.maxSelections && selected.length >= Number(screen.maxSelections)) {
          setInlineError(config.ui.maxReached);
          return;
        }
        selected.push(answerId);
      }
      state.answers[screen.id] = selected;
      state.answerLabels[screen.id] = selected.map(id => getAnswerLabel(screen, id));
      state.resultId = null;
      setInlineError('');
      refreshMultiButtons(screen);
    }

    function refreshMultiButtons(screen) {
      const selected = normalizedArray(state.answers[screen.id]);
      const atLimit = Boolean(screen.maxSelections && selected.length >= Number(screen.maxSelections));
      const countNode = app.querySelector('[data-selection-count]');
      if (countNode) {
        const max = Number(screen.maxSelections || screen.answers.length);
        countNode.textContent = `${selected.length} / ${max} sélectionnée${selected.length > 1 ? 's' : ''}`;
      }
      app.querySelectorAll('[data-answer-id]').forEach(button => {
        const active = selected.includes(button.dataset.answerId);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
        button.disabled = atLimit && !active;
      });
    }

    function setInlineError(message) {
      const node = app.querySelector('[data-inline-error]');
      if (node) node.textContent = message || '';
    }

    function getAnswerLabel(screen, id) {
      return screen.answers?.find(a => a.id === id)?.label || id;
    }

    function recordAnswer(screen, id, label) {
      state.answers[screen.id] = id;
      state.answerLabels[screen.id] = label;
      state.resultId = null;
      emit('QuizAnswer', { screen_id: screen.id, answer_id: id, answer_label: label });
    }

    function nextScreen() {
      state.screenIndex += 1;
      if (state.screenIndex >= config.screens.length) return startProcessing();
      state.phase = 'screen';
      render();
    }

    function goBack() {
      if (state.screenIndex <= 0) return;
      state.screenIndex -= 1;
      state.phase = 'screen';
      render();
    }

    function calculateScores(upToScreenId = null) {
      const totals = {};
      for (const screen of config.screens) {
        const value = state.answers[screen.id];
        if (value != null && (screen.kind === 'single' || screen.kind === 'multi')) {
          const chosen = screen.kind === 'multi' ? normalizedArray(value) : [value];
          for (const id of chosen) {
            const scores = screen.answers?.find(a => a.id === id)?.scores;
            if (scores) {
              Object.entries(scores).forEach(([key, amount]) => {
                totals[key] = (totals[key] || 0) + Number(amount || 0);
              });
            }
          }
        }
        if (upToScreenId && screen.id === upToScreenId) break;
      }
      return totals;
    }

    function primaryTheme(upToScreenId = null) {
      const scores = calculateScores(upToScreenId);
      const maximum = Math.max(0, ...THEME_KEYS.map(key => scores[key] || 0));
      const tied = THEME_KEYS.filter(key => (scores[key] || 0) === maximum);
      if (tied.length === 1) return tied[0];
      // When themes tie, respect the first message she selected in Q2.
      for (const id of normalizedArray(state.answers.q02_message)) {
        if (tied.includes(MESSAGE_THEME[id])) return MESSAGE_THEME[id];
      }
      // Otherwise use the first motivation from Q1, then a stable fallback.
      const q1 = {
        doubt: 'strength', mold: 'own_path',
        new_chapter: 'pride', just_because: 'pride'
      }[state.answers.q01_trigger];
      return tied.includes(q1) ? q1 : (tied[0] || 'strength');
    }

    function getMidpointInsight() {
      const theme = primaryTheme('q04_gift');
      const effort = Number(state.answers.q03_words || 3);
      const firstGiftPriority = normalizedArray(state.answers.q04_gift)[0];
      return {
        main: config.midpoint.insights?.[theme] || config.midpoint.insights.strength,
        body: config.midpoint.giftPriorityBodies?.[firstGiftPriority] || config.midpoint.body,
        extra: effort <= 2 ? config.midpoint.wordsHard :
          effort >= 4 ? config.midpoint.wordsEasy : ''
      };
    }

    function selectResult() {
      if (state.answers.q06_rings === 'never') return 'not_ring';
      return primaryTheme();
    }

    function startProcessing() {
      state.phase = 'processing';
      render();
    }

    function renderProcessing() {
      const messages = config.processing.messages || [];
      app.innerHTML = `<main class="sfrmdq-shell"><section class="sfrmdq-panel sfrmdq-processing">
        <p class="sfrmdq-kicker">${escapeHtml(config.processing.title)}</p>
        <div class="sfrmdq-processing__number" data-processing-number>0%</div>
        <p class="sfrmdq-processing__message" data-processing-message>${escapeHtml(messages[0] || '')}</p>
        <div class="sfrmdq-processing__track" aria-hidden="true"><span data-processing-bar></span></div>
      </section></main>`;
      const duration = Math.max(250, Number(config.processingMs || 1250));
      const start = performance.now();
      const number = app.querySelector('[data-processing-number]');
      const bar = app.querySelector('[data-processing-bar]');
      const description = app.querySelector('[data-processing-message]');
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      if (reduceMotion) {
        window.setTimeout(completeQuiz, 250);
        return;
      }
      function frame(time) {
        if (state.phase !== 'processing') return;
        const ratio = Math.min(1, (time - start) / duration);
        const progress = Math.round(100 * (1 - Math.pow(1 - ratio, 3)));
        if (number) number.textContent = `${progress}%`;
        if (bar) bar.style.width = `${progress}%`;
        if (description && messages.length) {
          description.textContent = progress < 38 ? messages[0] :
            progress < 82 ? (messages[1] || messages[0]) :
              (messages[2] || messages[messages.length - 1]);
        }
        if (ratio < 1) window.requestAnimationFrame(frame);
        else window.setTimeout(completeQuiz, 120);
      }
      window.requestAnimationFrame(frame);
    }

    function completeQuiz() {
      if (state.phase !== 'processing') return;
      state.phase = 'result';
      state.resultId = selectResult();
      state.completedAt = new Date().toISOString();
      emit('QuizComplete', { result_id: state.resultId, scores: calculateScores() });
      sendQuizCompletionData();
      renderResult();
    }

    function getRecommendationImage(objectionId) {
      const imageMap = {
        size: config.images.size || config.images.worn,
        quality: config.images.quality,
        message: config.images.card,
        wear: config.images.worn,
        delivery: config.images.giftBox
      };
      return imageMap[objectionId] || config.images.defaultResult || null;
    }

    function reviewHtml() {
      const review = config.review;
      if (!review || (!String(review.quote || '').trim() && !review.image)) return '';
      return `<div class="sfrmdq-review">
        ${review.image ? `<img src="${escapeHtml(review.image)}" alt="" loading="lazy">` : ''}
        <div>${review.showStars ? '<div class="sfrmdq-stars" aria-label="5 étoiles">★★★★★</div>' : ''}
          ${String(review.quote || '').trim() ? `<blockquote>« ${escapeHtml(review.quote)} »</blockquote>` : ''}
          ${review.author ? `<cite>${escapeHtml(review.author)}</cite>` : ''}
        </div>
      </div>`;
    }

    function renderResult() {
      const resultId = state.resultId || selectResult();
      const isMismatch = resultId === 'not_ring';
      const result = config.results[resultId] || config.results.strength;
      const objection = state.answers.q08_objection || 'none';
      const destination = isMismatch ? config.alternativeCollection?.url : config.productUrl;
      const destinationType = isMismatch ? 'collection' : 'product';
      const destinationHref = buildDestinationHref(destination, resultId);
      const primaryImage = isMismatch ? config.alternativeCollection?.image : getRecommendationImage(objection);
      const occasionImage = {
        birthday: config.images.giftBox,
        holiday: config.images.giftBox,
        transition: config.images.giftBox,
        difficult: config.images.card,
        none: config.images.worn
      }[state.answers.q07_occasion];
      const secondImage = isMismatch ? null :
        [occasionImage, config.images.card].find(image => image && image !== primaryImage) || null;
      const messages = normalizedArray(state.answers.q02_message).map(id => config.resultMessages[id]).filter(Boolean);
      const reassurance = config.resultDetails.objections?.[objection];

      app.innerHTML = `<main class="sfrmdq-shell"><section class="sfrmdq-result-card">
        <div class="sfrmdq-result-summary">
          <p class="sfrmdq-kicker">${escapeHtml(result.eyebrow)}</p>
          <h1 class="sfrmdq-result-title">${escapeHtml(result.headline)}</h1>
          <p class="sfrmdq-copy">${escapeHtml(result.body)}</p>
          ${messages.length ? `<div class="sfrmdq-reflection">
            <small>${escapeHtml(config.ui.resultReflectionLabel)}</small>
            <strong>${messages.map(escapeHtml).join('<span class="sfrmdq-dot"> · </span>')}</strong>
          </div>` : ''}
        </div>
        <div class="sfrmdq-result-separator" aria-hidden="true"></div>
        <div class="sfrmdq-recommendation">
          <div class="sfrmdq-recommendation-layout">
            <div class="sfrmdq-recommendation-copy">
              <p class="sfrmdq-kicker">${escapeHtml(isMismatch ? 'UN CADEAU QUI LUI RESSEMBLE' : config.ui.recommendationLabel)}</p>
              <h2 class="sfrmdq-product-title">${escapeHtml(isMismatch ? config.alternativeCollection.title : config.recommendation.productTitle)}</h2>
              ${!isMismatch ? `<p class="sfrmdq-copy">${escapeHtml(config.recommendation.body)}</p>
                ${reassurance ? `<div class="sfrmdq-reassurance"><span aria-hidden="true">✓</span><p>${escapeHtml(reassurance)}</p></div>` : ''}` :
                '<p class="sfrmdq-copy">Découvrez plutôt nos idées de cadeaux pour votre fille, sans chercher à lui imposer une bague qu’elle ne porterait pas.</p>'}
              <div class="sfrmdq-actions">
                <a class="sfrmdq-btn" href="${escapeHtml(destinationHref)}" data-action="pdp">
                  ${escapeHtml(isMismatch ? 'Voir les cadeaux pour fille →' : config.recommendation.cta)}
                </a>
              </div>
            </div>
            ${(primaryImage || secondImage) ? `<div class="sfrmdq-product-visuals">
              ${primaryImage ? `<img class="sfrmdq-main-product-image" src="${escapeHtml(primaryImage)}"
                  alt="${escapeHtml(isMismatch ? config.alternativeCollection.title : config.recommendation.productTitle)}" loading="lazy">` : ''}
              ${secondImage ? `<img class="sfrmdq-card-product-image" src="${escapeHtml(secondImage)}"
                  alt="Carte message incluse, Nage à contre-courant" loading="lazy">` : ''}
            </div>` : ''}
          </div>
          ${isMismatch ? '' : reviewHtml()}
        </div>
      </section></main>`;

      emit('QuizResult', {
        result_id: resultId,
        scores: calculateScores(),
        main_objection: objection,
        destination_type: destinationType
      });
      app.querySelector('[data-action="pdp"]')?.addEventListener('click', () => {
        emit('QuizPDPClick', {
          result_id: resultId,
          main_objection: objection,
          destination_type: destinationType
        });
      });
    }

    function sendQuizCompletionData() {
      if (completionDataSent || !config.dataWebhookUrl) return;
      completionDataSent = true;
      const params = new URLSearchParams(window.location.search);
      const payload = {
        quiz_session_id: quizSessionId,
        quiz_id: config.quizId,
        quiz_version: config.version,
        started_at: state.startedAt,
        completed_at: state.completedAt,
        result_id: state.resultId,
        main_objection: state.answers.q08_objection || null,
        emotional_intention: normalizedArray(state.answerLabels.q02_message)[0] || null,
        answers: state.answers,
        answer_labels: state.answerLabels,
        page_url: window.location.href,
        referrer: document.referrer || null,
        utm_source: params.get('utm_source'),
        utm_medium: params.get('utm_medium'),
        utm_campaign: params.get('utm_campaign'),
        utm_content: params.get('utm_content'),
        utm_term: params.get('utm_term'),
        fbclid: params.get('fbclid'),
        gclid: params.get('gclid'),
        ttclid: params.get('ttclid')
      };
      const form = new URLSearchParams();
      form.set('payload', JSON.stringify(payload));
      if (typeof navigator.sendBeacon === 'function') {
        try {
          if (navigator.sendBeacon(config.dataWebhookUrl, form)) return;
        } catch (_) { /* fall through to fetch */ }
      }
      fetch(config.dataWebhookUrl, { method: 'POST', body: form, mode: 'no-cors', keepalive: true })
        .catch(() => { /* optional integration */ });
    }

    function buildDestinationHref(baseUrl, resultId) {
      let target;
      try { target = new URL(baseUrl || '/', window.location.origin); }
      catch (_) { target = new URL('/', window.location.origin); }
      const source = new URL(window.location.href);
      for (const [key, value] of source.searchParams.entries()) {
        if (TRACKING_PARAMS.has(key) || key.startsWith('utm_')) target.searchParams.set(key, value);
      }
      target.searchParams.set('quiz_id', config.quizId);
      target.searchParams.set('quiz_version', config.version);
      target.searchParams.set('quiz_result', resultId);
      target.searchParams.set('quiz_session_id', quizSessionId);
      return target.origin === window.location.origin ?
        `${target.pathname}${target.search}${target.hash}` : target.href;
    }

    emit('QuizView');
    render();
  }

  function bootWithin(container = document) {
    container.querySelectorAll(ROOT_SELECTOR).forEach(initQuiz);
    if (container.matches?.(ROOT_SELECTOR)) initQuiz(container);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bootWithin(document), { once: true });
  } else {
    bootWithin(document);
  }
  document.addEventListener('shopify:section:load', (event) => bootWithin(event.target));
})();
